import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Autocomplete,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Person,
  Description,
  Build,
  Star,
  Phone,
  Email
} from '@mui/icons-material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { toast } from 'react-toastify';
import { translateTextStream } from '../../services/aiTranslateService';
import { retryAIAnalysis } from '../../services/instagramShowcaseService';

// Import existing services
import { getSubServiceTypes } from '../../services/subServiceTypeService';
import { getAreas, updateBusinessProfile } from '../../services/profileService';
import { updateStylistDetails, getStylistDetails } from '../../services/stylistService';
import AddressAutocomplete from '../common/AddressAutocomplete';
import AreaSuggestion from '../common/AreaSuggestion';
import type { AcceptSuggestionPayload, AreaSuggestionMatchResult } from '../common/AreaSuggestion';
import { matchPlaceToAreaWithAutoSelect } from '../../utils/areaMatchingUtils';
import type { RegionData as AreaUtilRegionData, MatchWithAutoSelectResult } from '../../utils/areaMatchingUtils';
import { normalizePhoneNumber, validateHKPhoneNumber, formatPhoneForStorage } from '../../utils/phoneUtils';
import type { FormDataValue } from '../../services/api';
import type { BusinessProfile } from '@app-types/models/business';

interface SubServiceTypeOption {
  _id: string;
  name: string;
  zh_name: string;
  [key: string]: unknown;
}

interface FlattenedArea {
  _id: string;
  location_id: string;
  chinese: string;
  english: string;
  location_code?: string;
  region_chinese?: string;
  region_english?: string;
}

interface AreaRegion {
  _id?: string;
  region_chinese?: string;
  region_english?: string;
  locations?: Array<{
    _id: string;
    chinese: string;
    english: string;
    location_code?: string;
    [key: string]: unknown;
  }>;
  [key: string]: unknown;
}

interface PlaceSelectedData {
  address?: string;
  formatted_address?: string;
  coordinates?: number[] | [number, number] | null;
  lat?: number;
  lng?: number;
  place_id?: string;
}

interface StaffProfile {
  displayName?: string;
  instagramUsername?: string;
  bio?: string;
  description?: string;
  zh_description?: string;
  businessAddress?: string;
  businessId?: { businessAddress?: string; phone?: string; [key: string]: unknown } | string;
  phone?: string;
  phoneNumber?: string;
  yearsOfExperience?: number;
  subServiceTypes?: SubServiceTypeOption[];
  featuredSkills?: Array<string | { name?: string; zh_name?: string; title?: string }>;
  businessArea?: FlattenedArea | string;
  aiAnalysisStatus?: string;
  aiAnalysisError?: string;
  [key: string]: unknown;
}

interface InstagramShowcaseFlowData {
  staffId?: string | null;
  instagramOnboardingToken?: string | null;
  aiAnalysisComplete?: boolean;
  isAnalyzing?: boolean;
  instagramConnected?: boolean;
  instagramToken?: string | null;
  instagramUserId?: string | null;
  instagramUsername?: string | null;
  instagramPosts?: Array<{ id: string; [key: string]: unknown }>;
  businessId?: string | null;
  email?: string;
  [key: string]: unknown;
}

interface ProfileEditStepProps {
  flowData: InstagramShowcaseFlowData;
  onUpdate: (data: Partial<InstagramShowcaseFlowData>) => void;
  onNext: () => void;
  onBack: () => void;
  onError: (message: string) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
}

interface ProfileFormData {
  displayName: string;
  description: string;
  zh_description: string;
  businessAddress: string;
  coordinates: number[];
  placeId: string;
  businessArea: FlattenedArea | null;
  subServiceTypes: SubServiceTypeOption[];
  featuredSkills: string[];
  phoneNumber: string;
  yearsOfExperience: number;
}

const ProfileEditStep = ({
  flowData,
  onUpdate,
  onNext,
  onBack,
  onError,
  isLoading,
  setIsLoading
}: ProfileEditStepProps): React.JSX.Element => {
  const { t } = useTranslation();
  const [subServiceTypes, setSubServiceTypes] = useState<SubServiceTypeOption[]>([]);
  const [isLoadingSubServiceTypes, setIsLoadingSubServiceTypes] = useState<boolean>(true);
  const [areas, setAreas] = useState<AreaRegion[]>([]);
  const [flattenedAreas, setFlattenedAreas] = useState<FlattenedArea[]>([]);
  const [areaSuggestion, setAreaSuggestion] = useState<MatchWithAutoSelectResult | null>(null);
  const [isTranslatingEnToZh, setIsTranslatingEnToZh] = useState<boolean>(false);
  const [isTranslatingZhToEn, setIsTranslatingZhToEn] = useState<boolean>(false);
  const isLoadingAiDataRef = useRef<boolean>(false);
  const [pendingBusinessAreaId, setPendingBusinessAreaId] = useState<string | null>(null);

  // AI Analysis error recovery states
  const [aiAnalysisError, setAiAnalysisError] = useState<string | null>(null);
  const [isRetryingAI, setIsRetryingAI] = useState<boolean>(false);
  const [manualEntryMode, setManualEntryMode] = useState<boolean>(false);

  // Form initialization - data will be loaded from database via fetchStaffProfileData
  const { control, handleSubmit, setValue, watch, formState: { errors } } = useForm<ProfileFormData>({
    defaultValues: {
      displayName: '',
      description: '',
      zh_description: '',
      businessAddress: '',
      coordinates: [],
      placeId: '',
      businessArea: null,
      subServiceTypes: [],
      featuredSkills: [],
      phoneNumber: '',
      yearsOfExperience: 1
    }
  });

  // Fetch staff profile data function
  // Uses permanent auth token (localStorage.token) if available, otherwise Instagram onboarding token
  const fetchStaffProfileData = useCallback(async (): Promise<StaffProfile | null> => {
    if (!flowData.staffId) {
      return null;
    }

    if (isLoadingAiDataRef.current) {
      return null;
    }

    // Determine which token to use
    const permanentToken = localStorage.getItem('token');
    const instagramToken = flowData.instagramOnboardingToken;

    // Check if permanent token exists and is different from Instagram token (i.e., after auto-login/claim)
    const hasPermanentAuth = permanentToken && permanentToken !== instagramToken;

    if (!hasPermanentAuth && !instagramToken) {
      return null;
    }

    // For getStylistDetails (protected endpoint), we need proper authentication
    // Ensure the correct token is set in localStorage for API interceptor
    if (!hasPermanentAuth && instagramToken) {
      const currentToken = localStorage.getItem('token');
      if (currentToken !== instagramToken) {
        localStorage.setItem('token', instagramToken);
      }
    }

    isLoadingAiDataRef.current = true;

    try {
      const stylistResult = await getStylistDetails(flowData.staffId!);
      const staffProfile = stylistResult as unknown as StaffProfile;
      return staffProfile;
    } catch (error: unknown) {
      console.error('❌ Error fetching staff profile:', error);
      throw error;
    } finally {
      // Don't remove token - let it persist for subsequent API calls in the flow
      // Token will be naturally replaced when account is claimed or user logs in
      isLoadingAiDataRef.current = false;
    }
  }, [flowData.staffId, flowData.instagramOnboardingToken]);

  // Fetch staff profile data and prefill form - simplified single fetch
  useEffect(() => {
    // Guard clause: Don't fetch if AI analysis is still running
    if (!flowData.aiAnalysisComplete && flowData.isAnalyzing) {
      return;
    }

    const processStaffProfile = async () => {
      try {
        const staffProfile = await fetchStaffProfileData();

        // Note: fetchStaffProfileData returns null for non-error cases (already loading, no auth yet)
        // Only proceed with data population if we actually received profile data
        if (!staffProfile) {
          return;
        }

        // Check if AI analysis failed
        if (staffProfile.aiAnalysisStatus === 'failed' || staffProfile.aiAnalysisError) {
          setAiAnalysisError('analysis_failed');
          // Still continue to prefill with any existing data
        }

        // Pre-populate all fields from database - data is guaranteed fresh from AI analysis
        const displayName = staffProfile.displayName || staffProfile.instagramUsername;
        if (displayName) {
          setValue('displayName', displayName, { shouldDirty: false });
        }

        // Pre-populate bio/description
        if (staffProfile.bio) {
          setValue('description', staffProfile.bio, { shouldDirty: false });
        } else if (staffProfile.description) {
          setValue('description', staffProfile.description, { shouldDirty: false });
        }

        // Pre-populate Chinese description
        if (staffProfile.zh_description) {
          setValue('zh_description', staffProfile.zh_description, { shouldDirty: false });
        }

        // Pre-populate business address
        const businessAddress = staffProfile.businessAddress ||
                               (typeof staffProfile.businessId === 'object' && staffProfile.businessId?.businessAddress);
        if (businessAddress) {
          setValue('businessAddress', businessAddress, { shouldDirty: false });
        }

        // Pre-populate phone number
        const phoneNumber = staffProfile.phone || staffProfile.phoneNumber ||
                           (typeof staffProfile.businessId === 'object' && staffProfile.businessId?.phone);
        if (phoneNumber) {
          setValue('phoneNumber', normalizePhoneNumber(phoneNumber), { shouldDirty: false });
        }

        // Pre-populate years of experience
        if (staffProfile.yearsOfExperience) {
          setValue('yearsOfExperience', staffProfile.yearsOfExperience, { shouldDirty: false });
        }

        // Pre-populate sub service types (critical field)
        if (staffProfile.subServiceTypes && staffProfile.subServiceTypes.length > 0) {
          setValue('subServiceTypes', staffProfile.subServiceTypes, { shouldDirty: false });
        }

        // Pre-populate featured skills
        if (staffProfile.featuredSkills && staffProfile.featuredSkills.length > 0) {
          const skillsAsStrings = staffProfile.featuredSkills.map(skill => {
            if (typeof skill === 'string') return skill;
            return skill.name || skill.zh_name || skill.title || String(skill);
          });
          setValue('featuredSkills', skillsAsStrings, { shouldDirty: false });
        }

        // Pre-populate business area - handle both object and string (ObjectId) formats
        if (staffProfile.businessArea) {
          // Extract the ID regardless of format
          const businessAreaId = typeof staffProfile.businessArea === 'string'
            ? staffProfile.businessArea
            : staffProfile.businessArea._id;

          if (businessAreaId && flattenedAreas.length > 0) {
            // flattenedAreas already loaded, hydrate immediately
            const hydratedArea = flattenedAreas.find(a => a._id === businessAreaId);
            if (hydratedArea) {
              setValue('businessArea', hydratedArea, { shouldDirty: false });
            }
          } else if (businessAreaId) {
            // flattenedAreas not loaded yet, store ID for deferred hydration
            setPendingBusinessAreaId(businessAreaId);
          }
        }

      } catch (error: unknown) {
        console.error('❌ Error in processStaffProfile:', error);
        const err = error as { response?: { data?: { message?: string } }; message?: string };
        const errorMessage = err.response?.data?.message || err.message || '';

        // Check if token is expired or invalid
        if (errorMessage.includes('expired') || errorMessage.includes('Invalid or expired')) {
          localStorage.removeItem('instagram_onboarding_token');

          // Reset flow data to force reconnection
          onUpdate({
            instagramConnected: false,
            instagramToken: null,
            instagramOnboardingToken: null,
            instagramUserId: null,
            instagramUsername: null,
            instagramPosts: []
          });

          toast.error(t('instagramShowcase.claim.tokenExpired', 'Instagram session expired. Please reconnect your Instagram account.'));
          onBack(); // Go back to reconnect
        } else {
          setAiAnalysisError('fetch_failed');
          setManualEntryMode(true);
          toast.error(t('instagramShowcase.profile.fetchError', 'Failed to load profile data. Please enter manually or retry.'));
        }
      }
    };

    processStaffProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flowData.staffId, flowData.instagramOnboardingToken, flowData.aiAnalysisComplete, flowData.isAnalyzing, setValue, fetchStaffProfileData, t]);

  // Handle AI translation
  const handleStreamTranslate = useCallback(async (
    sourceText: string,
    sourceLang: string,
    targetLang: string,
    targetFieldName: keyof ProfileFormData,
    setLoadingState: (loading: boolean) => void
  ): Promise<void> => {
    if (!sourceText) {
      toast.info('Source field for translation is empty.');
      return;
    }
    setLoadingState(true);

    try {
      const formCompatibleSetTargetState = (valueOrUpdater: string | ((prev: string) => string)): void => {
        const currentFieldValue = (watch(targetFieldName) as string) || '';
        if (typeof valueOrUpdater === 'function') {
          setValue(targetFieldName, valueOrUpdater(currentFieldValue) as ProfileFormData[typeof targetFieldName], { shouldDirty: true, shouldTouch: true });
        } else {
          setValue(targetFieldName, valueOrUpdater as ProfileFormData[typeof targetFieldName], { shouldDirty: true, shouldTouch: true });
        }
      };

      await translateTextStream(
        sourceText,
        targetLang as 'en' | 'zh' | 'zh-HK' | 'zh-TW',
        sourceLang as 'en' | 'zh' | 'zh-HK' | 'zh-TW',
        formCompatibleSetTargetState,
        () => {
          setLoadingState(false);
          toast.success('Translation streamed successfully!');
        },
        (error: { message?: string }) => {
          setLoadingState(false);
          toast.error('Translation failed: ' + (error.message || 'Unknown error'));
        }
      );
    } catch (error: unknown) {
      setLoadingState(false);
      const err = error as { message?: string };
      toast.error('Translation failed: ' + (err.message || 'Unknown error'));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setValue, watch]);

  // Helper function to check if field has AI-populated data
  const hasAiData = (fieldName: keyof ProfileFormData): boolean => {
    const value = watch(fieldName);
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return !!value;
  };

  // Handle AI analysis retry
  const handleRetryAIAnalysis = async (): Promise<void> => {
    if (!flowData.staffId || !flowData.instagramOnboardingToken) {
      toast.error(t('instagramShowcase.profile.missingData', 'Missing required data to retry AI analysis'));
      return;
    }

    setIsRetryingAI(true);
    setAiAnalysisError(null);

    try {
      const result = await retryAIAnalysis(flowData.staffId, flowData.instagramOnboardingToken);

      if (result.success) {
        toast.success(t('instagramShowcase.profile.aiRetrySuccess', 'AI analysis completed successfully!'));
        setManualEntryMode(false);

        // Refetch profile data to get updated AI analysis
        const staffProfile = await fetchStaffProfileData();
        if (staffProfile) {
          // Re-populate form with new AI data
          if (staffProfile.displayName) {
            setValue('displayName', staffProfile.displayName, { shouldDirty: false });
          }
          if (staffProfile.description) {
            setValue('description', staffProfile.description, { shouldDirty: false });
          }
          if (staffProfile.zh_description) {
            setValue('zh_description', staffProfile.zh_description, { shouldDirty: false });
          }
          // Add more field updates as needed
        }
      } else {
        throw new Error('Retry failed');
      }
    } catch (error: unknown) {
      console.error('❌ AI retry failed:', error);
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = err.response?.data?.message || err.message || '';

      // Check if token is expired or invalid
      if (errorMessage.includes('expired') || errorMessage.includes('Invalid or expired')) {
        localStorage.removeItem('instagram_onboarding_token');

        // Reset flow data to force reconnection
        onUpdate({
          instagramConnected: false,
          instagramToken: null,
          instagramOnboardingToken: null,
          instagramUserId: null,
          instagramUsername: null,
          instagramPosts: []
        });

        toast.error(t('instagramShowcase.claim.tokenExpired', 'Instagram session expired. Please reconnect your Instagram account.'));
        onBack(); // Go back to reconnect
      } else {
        toast.error(t('instagramShowcase.profile.aiRetryFailed', 'Failed to retry AI analysis. Please enter manually.'));
        setAiAnalysisError('retry_failed');
        setManualEntryMode(true);
      }
    } finally {
      setIsRetryingAI(false);
    }
  };

  // Fetch sub-service types
  useEffect(() => {
    const fetchSubServiceTypes = async (): Promise<void> => {
      try {
        const response = await getSubServiceTypes();
        setSubServiceTypes((response.subServiceTypes || []) as SubServiceTypeOption[]);
      } catch (error: unknown) {
        console.error('Error fetching sub-service types:', error);
      } finally {
        setIsLoadingSubServiceTypes(false);
      }
    };
    fetchSubServiceTypes();
  }, []);

  // Fetch areas
  useEffect(() => {
    const fetchAreas = async (): Promise<void> => {
      try {
        const areaData = await getAreas() as AreaRegion[];
        setAreas(areaData);

        const flattened: FlattenedArea[] = [];
        areaData.forEach((region: AreaRegion) => {
          if (region.locations && Array.isArray(region.locations)) {
            region.locations.forEach((location: { _id: string; chinese: string; english: string; location_code?: string }) => {
              flattened.push({
                _id: location._id,
                location_id: location._id,
                chinese: location.chinese,
                english: location.english,
                location_code: location.location_code,
                region_chinese: region.region_chinese,
                region_english: region.region_english
              });
            });
          }
        });
        setFlattenedAreas(flattened);
      } catch (error: unknown) {
        console.error('Error fetching areas:', error);
      }
    };
    fetchAreas();
  }, []);

  // Hydrate businessArea when flattenedAreas becomes available (deferred hydration)
  useEffect(() => {
    if (pendingBusinessAreaId && flattenedAreas.length > 0) {
      const hydratedArea = flattenedAreas.find(a => a._id === pendingBusinessAreaId);
      if (hydratedArea) {
        setValue('businessArea', hydratedArea, { shouldDirty: false });
      }
      setPendingBusinessAreaId(null); // Clear after hydration attempt
    }
  }, [pendingBusinessAreaId, flattenedAreas, setValue]);

  const handlePlaceSelected = (placeData: PlaceSelectedData): void => {
    const coords = placeData.coordinates || [placeData.lng ?? 0, placeData.lat ?? 0];
    setValue('coordinates', coords as number[]);
    setValue('placeId', placeData.place_id || '');

    if (areas.length > 0) {
      const areaMatchResult = matchPlaceToAreaWithAutoSelect(
        { address: placeData.address || placeData.formatted_address || '', ...placeData },
        areas as AreaUtilRegionData[]
      );

      if (areaMatchResult.shouldAutoSelect && areaMatchResult.match) {
        const { match } = areaMatchResult;
        // Find full object from flattenedAreas
        const fullArea = flattenedAreas.find(a => a._id === match.location_id);
        setValue('businessArea', fullArea || null, { shouldValidate: true });
        toast.success(t('areaSuggestion.autoSelected', `Area auto-selected: ${match.chinese || match.english}`));
      } else if (areaMatchResult.shouldShowSuggestion) {
        setAreaSuggestion(areaMatchResult);
      }
    }
  };

  const handleAreaSuggestionAccept = (payload: AcceptSuggestionPayload): void => {
    const { match } = payload;
    // Find full object from flattenedAreas
    const fullArea = flattenedAreas.find(a => a._id === payload.locationId);
    setValue('businessArea', fullArea || null, { shouldValidate: true });
    setAreaSuggestion(null);
    toast.success(t('areaSuggestion.accepted', `Area accepted: ${match.chinese || match.english}`));
  };

  const handleAreaSuggestionDismiss = (): void => {
    setAreaSuggestion(null);
  };

  const onSubmit = async (data: ProfileFormData): Promise<void> => {
    setIsLoading(true);
    onError('');

    try {
      // Format phone number: if user entered 8 digits, add +852 prefix
      if (data.phoneNumber && /^\d{8}$/.test(data.phoneNumber)) {
        data.phoneNumber = `+852${data.phoneNumber}`;
      }

      // Prepare business profile data - extract _id from businessArea object
      const businessProfileData: Record<string, unknown> = {
        businessAddress: data.businessAddress,
        coordinates: data.coordinates as [number, number],
        placeId: data.placeId,
        businessArea: data.businessArea?._id
      };

      // Prepare stylist profile data
      const stylistData: Record<string, unknown> = {
        displayName: data.displayName,
        description: data.description,
        zh_description: data.zh_description,
        subServiceTypes: data.subServiceTypes,
        featuredSkills: data.featuredSkills || [],
        phoneNumber: data.phoneNumber ? formatPhoneForStorage(data.phoneNumber) : null,
        _id: flowData.staffId
      };

      // Update business profile and stylist details
      // Authentication token is already in localStorage (either permanent or Instagram onboarding)
      // API interceptor will automatically attach it to requests
      const hasPermanentToken = localStorage.getItem('token') && localStorage.getItem('token') !== flowData.instagramOnboardingToken;

      // For users who haven't claimed account yet, temporarily set Instagram onboarding token
      let tokenSwapped = false;
      if (!hasPermanentToken && flowData.instagramOnboardingToken) {
        const existingToken = localStorage.getItem('token');
        if (existingToken !== flowData.instagramOnboardingToken) {
          localStorage.setItem('token', flowData.instagramOnboardingToken);
          tokenSwapped = true;
        }
      }

      try {
        // Update business profile
        await updateBusinessProfile(businessProfileData as Partial<BusinessProfile>);

        // Update stylist details
        await updateStylistDetails(stylistData as Record<string, FormDataValue>);
      } finally {
        // Clean up: remove temporary token if we set it
        if (tokenSwapped) {
          localStorage.removeItem('token');
        }
      }

      // Update localStorage user information
      const existingUser = JSON.parse(localStorage.getItem('user') || '{}');
      const updatedUser = {
        ...existingUser,
        displayName: data.displayName,
        email: flowData.email,
        businessId: flowData.businessId,
        status: 'pending' // Keep as pending until email verification
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));

      // Update flow data
      onUpdate({
        ...data,
        profileComplete: true
      });

      toast.success(t('instagramShowcase.profile.success', 'Profile updated successfully!'));
      onNext();

    } catch (error: unknown) {
      console.error('Error updating profile:', error);
      const err = error as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = err.response?.data?.message || err.message || 'Failed to update profile';
      onError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box className="profile-edit-step">
      <Typography variant="h5" gutterBottom>
        {t('instagramShowcase.profile.title', 'Complete Your Profile')}
      </Typography>

      <Typography variant="body1" color="text.secondary" paragraph>
        {t('instagramShowcase.profile.description', 'Review and complete your profile information. AI has pre-filled details from your Instagram.')}
      </Typography>

      {/* Email Verification Reminder */}
      <Alert severity="warning" sx={{ mb: 3 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          <Email sx={{ mr: 1, verticalAlign: 'middle' }} />
          {t('instagramShowcase.profile.emailVerification', 'Don\'t forget to verify your email!')}
        </Typography>
        <Typography variant="body2">
          {t('instagramShowcase.profile.emailVerificationDesc', 'Check your email ({email}) and click the verification link to activate your account.', { email: flowData.email })}
        </Typography>
      </Alert>

      {/* AI Analysis Error Recovery */}
      {aiAnalysisError && !manualEntryMode && (
        <Alert
          severity="warning"
          sx={{ mb: 3 }}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                disabled={isRetryingAI}
                onClick={handleRetryAIAnalysis}
              >
                {isRetryingAI ? t('instagramShowcase.profile.retrying', 'Retrying...') : t('instagramShowcase.profile.retryAI', 'Retry AI Analysis')}
              </Button>
              <Button
                size="small"
                onClick={() => setManualEntryMode(true)}
              >
                {t('instagramShowcase.profile.enterManually', 'Enter Manually')}
              </Button>
            </Box>
          }
        >
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            {aiAnalysisError === 'analysis_failed'
              ? t('instagramShowcase.profile.aiAnalysisFailed', 'AI analysis failed to process your Instagram profile.')
              : aiAnalysisError === 'no_data'
              ? t('instagramShowcase.profile.noAiData', 'No AI analysis data available.')
              : t('instagramShowcase.profile.dataFetchFailed', 'Failed to load profile data.')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t('instagramShowcase.profile.aiErrorHelp', 'You can retry the AI analysis or enter your information manually.')}
          </Typography>
        </Alert>
      )}

      {/* Manual Entry Mode Info */}
      {manualEntryMode && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            📝 {t('instagramShowcase.profile.manualEntryMode', 'Manual entry mode: Please fill in your profile information below.')}
          </Typography>
        </Alert>
      )}

      <Divider sx={{ my: 3 }} />

      <form onSubmit={handleSubmit(onSubmit)} className="profile-form">
        <Stack spacing={3}>
          {/* Display Name */}
          <Controller
            name="displayName"
            control={control}
            rules={{
              required: t('instagramShowcase.profile.nameRequired', 'Display name is required'),
              minLength: { value: 2, message: t('instagramShowcase.profile.nameMinLength', 'Name must be at least 2 characters') }
            }}
            render={({ field }) => (
              <Box>
                <TextField
                  {...field}
                  fullWidth
                  label={t('instagramShowcase.profile.displayName', 'Display Name')}
                  placeholder={t('instagramShowcase.profile.namePlaceholder', 'How clients will see you')}
                  error={!!errors.displayName}
                  helperText={errors.displayName?.message}
                  InputProps={{
                    startAdornment: <Person color="action" sx={{ mr: 1 }} />,
                    endAdornment: hasAiData('displayName') && (
                      <AutoFixHighIcon color="primary" sx={{ fontSize: 20 }} titleAccess="AI suggested" />
                    )
                  }}
                />
                {hasAiData('displayName') && (
                  <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                    <AutoFixHighIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    {t('instagramShowcase.profile.aiSuggested', 'AI suggested from Instagram')}
                  </Typography>
                )}
              </Box>
            )}
          />

          {/* Sub-Service Types */}
          <Controller
            name="subServiceTypes"
            control={control}
            rules={{
              validate: value => value && value.length > 0 || t('instagramShowcase.profile.subServiceTypesRequired', 'Please select at least one service type')
            }}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.subServiceTypes}>
                <Autocomplete
                  multiple
                  disabled={isLoadingSubServiceTypes}
                  loading={isLoadingSubServiceTypes}
                  options={subServiceTypes}
                  value={field.value || []}
                  onChange={(_, newValue) => field.onChange(newValue)}
                  getOptionLabel={(option) => `${option.name || 'Unknown'} (${option.zh_name || 'Unknown'})`}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('instagramShowcase.profile.subServiceTypes', 'Service Types') + ' *'}
                      error={!!errors.subServiceTypes}
                      helperText={errors.subServiceTypes?.message}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <Build color="action" sx={{ mr: 1 }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                        endAdornment: (
                          <>
                            {hasAiData('subServiceTypes') && (
                              <AutoFixHighIcon color="primary" sx={{ fontSize: 20, mr: 1 }} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option._id === value._id}
                />
                {hasAiData('subServiceTypes') && (
                  <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                    <AutoFixHighIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    {t('instagramShowcase.profile.aiSuggested', 'AI suggested from Instagram')}
                  </Typography>
                )}
              </FormControl>
            )}
          />

          {/* Featured Skills */}
          <Controller
            name="featuredSkills"
            control={control}
            render={({ field }) => (
              <Box>
                <Autocomplete<string, true, false, true>
                  multiple
                  freeSolo
                  options={[] as string[]}
                  value={field.value || []}
                  onChange={(_, newValue) => {
                    const stringValues = newValue.map(value => {
                      if (typeof value === 'string') return value;
                      return String(value);
                    });
                    field.onChange(stringValues);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('instagramShowcase.profile.featuredSkills', 'Featured Skills')}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <Star color="action" sx={{ mr: 1 }} />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                        endAdornment: (
                          <>
                            {hasAiData('featuredSkills') && (
                              <AutoFixHighIcon color="primary" sx={{ fontSize: 20, mr: 1 }} />
                            )}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                />
                {hasAiData('featuredSkills') && (
                  <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                    <AutoFixHighIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    {t('instagramShowcase.profile.aiSuggested', 'AI suggested from Instagram')}
                  </Typography>
                )}
              </Box>
            )}
          />

          {/* Phone Number */}
          <Controller
            name="phoneNumber"
            control={control}
            rules={{
              validate: value => {
                if (!value) return true;
                return validateHKPhoneNumber(value) || t('instagramShowcase.profile.phoneInvalid', 'Please enter a valid Hong Kong phone number');
              }
            }}
            render={({ field }) => (
              <Box>
                <TextField
                  {...field}
                  fullWidth
                  label={t('instagramShowcase.profile.phoneNumber', 'Phone Number (Optional)')}
                  placeholder="+852XXXXXXXX"
                  error={!!errors.phoneNumber}
                  helperText={errors.phoneNumber?.message || t('instagramShowcase.profile.phoneHelp', 'Verified phones get 3x more bookings')}
                  InputProps={{
                    startAdornment: <Phone color="action" sx={{ mr: 1 }} />,
                    endAdornment: hasAiData('phoneNumber') && (
                      <AutoFixHighIcon color="primary" sx={{ fontSize: 20 }} />
                    )
                  }}
                />
                {hasAiData('phoneNumber') && (
                  <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                    <AutoFixHighIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    {t('instagramShowcase.profile.aiSuggested', 'AI suggested from Instagram')}
                  </Typography>
                )}
              </Box>
            )}
          />

          {/* Business Address */}
          <Controller
            name="businessAddress"
            control={control}
            rules={{
              required: t('instagramShowcase.profile.addressRequired', 'Business address is required')
            }}
            render={({ field }) => (
              <Box>
                <AddressAutocomplete
                  label={t('instagramShowcase.profile.businessAddress', 'Business Address')}
                  value={field.value}
                  onInputChange={(value: string) => field.onChange(value)}
                  onPlaceSelected={(placeData) => {
                    field.onChange(placeData.address);
                    handlePlaceSelected(placeData as PlaceSelectedData);
                  }}
                  error={!!errors.businessAddress}
                  helperText={errors.businessAddress?.message}
                />
                {hasAiData('businessAddress') && (
                  <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                    <AutoFixHighIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    {t('instagramShowcase.profile.aiSuggested', 'AI suggested from Instagram')}
                  </Typography>
                )}
                {areaSuggestion && (
                  <AreaSuggestion
                    matchResult={areaSuggestion as AreaSuggestionMatchResult}
                    onAcceptSuggestion={(payload) => {
                      handleAreaSuggestionAccept(payload);
                    }}
                    onDismiss={handleAreaSuggestionDismiss}
                  />
                )}
              </Box>
            )}
          />

          {/* Business Area */}
          <Controller
            name="businessArea"
            control={control}
            rules={{
              required: t('instagramShowcase.profile.areaRequired', 'Please select a business area')
            }}
            render={({ field }) => (
              <Box>
                <FormControl fullWidth error={!!errors.businessArea}>
                  <InputLabel>{t('instagramShowcase.profile.businessArea', 'Business Area')}</InputLabel>
                  <Select
                    value={field.value?._id || ''}
                    onChange={(e) => {
                      const selectedArea = flattenedAreas.find(a => a._id === e.target.value);
                      field.onChange(selectedArea || null);
                    }}
                    label={t('instagramShowcase.profile.businessArea', 'Business Area')}
                  >
                    {flattenedAreas.map((area) => (
                      <MenuItem key={area._id} value={area._id}>
                        {area.chinese || 'Unknown'} ({area.english || 'Unknown'})
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.businessArea && (
                    <FormHelperText>{errors.businessArea.message}</FormHelperText>
                  )}
                </FormControl>
                {hasAiData('businessArea') && (
                  <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                    <AutoFixHighIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    {t('instagramShowcase.profile.aiSuggested', 'AI suggested from Instagram')}
                  </Typography>
                )}
              </Box>
            )}
          />

          {/* Description Fields with Translation */}
          <Box sx={{ mt: 2, mb: 0.5, display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              onClick={() => {
                const currentDesc = watch('description');
                handleStreamTranslate(currentDesc, 'en', 'zh-HK', 'zh_description', setIsTranslatingEnToZh);
              }}
              disabled={isTranslatingEnToZh || isTranslatingZhToEn || isLoading || !watch('description')}
              variant="outlined"
              size="small"
              startIcon={isTranslatingEnToZh ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
            >
              {t('quickListingWizard.translateWithAI')} {t('common.toChinese')}
            </Button>
          </Box>

          <Controller
            name="description"
            control={control}
            rules={{
              required: t('instagramShowcase.profile.descriptionRequired', 'Please add a description'),
              maxLength: { value: 500, message: t('instagramShowcase.profile.descriptionMaxLength', 'Description must be less than 500 characters') }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                multiline
                rows={3}
                label={t('instagramShowcase.profile.description', 'About You (English)')}
                placeholder={t('instagramShowcase.profile.descriptionPlaceholder', 'Tell clients about your experience and specialties...')}
                error={!!errors.description}
                helperText={errors.description?.message || `${field.value.length}/500`}
                disabled={isTranslatingEnToZh || isTranslatingZhToEn}
                InputProps={{
                  startAdornment: <Description color="action" sx={{ mr: 1, alignSelf: 'flex-start', mt: 1 }} />
                }}
              />
            )}
          />

          <Box sx={{ mt: 1, mb: 0.5, display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              onClick={() => {
                const currentZhDesc = watch('zh_description');
                handleStreamTranslate(currentZhDesc, 'zh-HK', 'en', 'description', setIsTranslatingZhToEn);
              }}
              disabled={isTranslatingEnToZh || isTranslatingZhToEn || isLoading || !watch('zh_description')}
              variant="outlined"
              size="small"
              startIcon={isTranslatingZhToEn ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
            >
              {t('quickListingWizard.translateWithAI')} {t('common.toEnglish')}
            </Button>
          </Box>

          <Controller
            name="zh_description"
            control={control}
            rules={{
              required: t('instagramShowcase.profile.zhDescriptionRequired', 'Please add a Chinese description'),
              maxLength: { value: 500, message: t('instagramShowcase.profile.descriptionMaxLength', 'Description must be less than 500 characters') }
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                multiline
                rows={3}
                label={t('instagramShowcase.profile.zh_description', 'About You (Chinese)')}
                error={!!errors.zh_description}
                helperText={errors.zh_description?.message || `${field.value.length}/500`}
                disabled={isTranslatingEnToZh || isTranslatingZhToEn}
                InputProps={{
                  startAdornment: <Description color="action" sx={{ mr: 1, alignSelf: 'flex-start', mt: 1 }} />
                }}
              />
            )}
          />
        </Stack>

        {/* Navigation buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            variant="outlined"
            onClick={onBack}
            disabled={isLoading}
          >
            {t('instagramShowcase.profile.back', 'Back')}
          </Button>

          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={isLoading}
            sx={{ px: 4 }}
          >
            {isLoading
              ? t('instagramShowcase.profile.updating', 'Updating Profile...')
              : t('instagramShowcase.profile.continue', 'Continue to Services')
            }
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default ProfileEditStep;